import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import {
  Container, Typography, Paper, Button, Box, Alert, Snackbar, Chip, Divider,
  RadioGroup, FormControlLabel, Radio, Card, CardContent, CardActions,
  Grid, Tooltip, InputAdornment, Autocomplete, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, IconButton, MenuItem, Select, FormControl, InputLabel
} from '@mui/material';
import axios from 'axios';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import PaidIcon from '@mui/icons-material/Paid';
import PendingIcon from '@mui/icons-material/Pending';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import FilterListIcon from '@mui/icons-material/FilterList';
import RefreshIcon from '@mui/icons-material/Refresh';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import PersonIcon from '@mui/icons-material/Person';
import { MaterialReactTable } from 'material-react-table';
import { MRT_Localization_PT_BR } from 'material-react-table/locales/pt-BR';

const API_URL = process.env.REACT_APP_API_URL;

const FORM_VAZIO = {
  auto_infracao: '',
  situacao_detran: '',
  orgao_emissor: '',
  codigo_multa: '',
  descricao: '',
  data_infracao: '',
  data_vencimento: '',
  previsao_pagamento: '',
  valor: '',
  responsavel: 'cliente',
  observacoes: '',
  observacao_cobranca: '',
};

const STATUS_MAP = {
  pendente:         { label: 'Pendente',            color: 'warning' },
  pago:             { label: 'Pago',                color: 'success' },
  sem_retorno:      { label: 'Sem Retorno',         color: 'default' },
  boleto_gerado:    { label: 'Boleto Gerado',       color: 'info' },
  pago_locadora:    { label: 'Pago pela Locadora',  color: 'info' },
  cobrado_cliente:  { label: 'Cobrado do Cliente',  color: 'primary' },
  recebido_cliente: { label: 'Recebido do Cliente', color: 'success' },
  contestada:       { label: 'Contestada',          color: 'error' },
  cancelada:        { label: 'Cancelada',           color: 'default' },
};

const fmtData  = (v) => v ? new Date(v).toLocaleDateString('pt-BR') : '—';
const fmtValor = (v) => `R$ ${parseFloat(v || 0).toFixed(2).replace('.', ',')}`;

// ─────────────────────────────────────────────────────────────
export default function Multas() {

  // ── dados base
  const [veiculos, setVeiculos]   = useState([]);
  const [clientes, setClientes]   = useState([]);
  const [multas, setMultas]       = useState([]);
  const [carregando, setCarregando] = useState(false);

  // ── filtros da tabela principal
  const [filtroVeiculo, setFiltroVeiculo]         = useState('');
  const [filtroStatus, setFiltroStatus]           = useState('');
  const [filtroResponsavel, setFiltroResponsavel] = useState('');
  const [filtroDataInicio, setFiltroDataInicio]   = useState('');
  const [filtroDataFim, setFiltroDataFim]         = useState('');
  const [filtroPrevInicio, setFiltroPrevInicio]   = useState('');
  const [filtroPrevFim, setFiltroPrevFim]         = useState('');
  const [filtrosVisiveis, setFiltrosVisiveis]     = useState(false);

  // ── PAINEL SPLIT VIEW ──────────────────────────────────────
  const [painelAberto, setPainelAberto]           = useState(false);
  const [splitRatio, setSplitRatio]               = useState(42); // % lado esquerdo
  const splitContainerRef                         = useRef(null);

  // Lado esquerdo (consulta)
  const [veiculoConsulta, setVeiculoConsulta]     = useState(null);
  const [consultaCarregando, setConsultaCarregando] = useState(false);
  const [consultaResultado, setConsultaResultado] = useState(null);

  // Lado direito (formulário)
  const [modoEdicao, setModoEdicao]               = useState(false);
  const [multaEditId, setMultaEditId]             = useState(null);
  const [formVeiculoId, setFormVeiculoId]         = useState('');
  const [formCliente, setFormCliente]             = useState(null);
  const [form, setFormState]                      = useState(FORM_VAZIO);

  // ── dialogs secundários
  const [multaSelecionada, setMultaSelecionada]   = useState(null);
  const [dialogDetalhes, setDialogDetalhes]       = useState(false);
  const [dialogCondutor, setDialogCondutor]       = useState({ aberto: false, novoCliente: null, obs: '' });
  const [dialogExclusao, setDialogExclusao]       = useState({ aberto: false, multa: null });

  // ── snackbar
  const [snackbar, setSnackbar] = useState({ open: false, mensagem: '', tipo: 'success' });
  const toast = (mensagem, tipo = 'success') => setSnackbar({ open: true, mensagem, tipo });

  const hdr = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  // ── helper para campo do formulário
  const setField = (f, v) => setFormState(s => ({ ...s, [f]: v }));

  // ── carregamento inicial
  const recarregarMultas = useCallback(async () => {
    try {
      const params = {};
      if (filtroVeiculo)     params.veiculo_id  = filtroVeiculo;
      if (filtroStatus)      params.status      = filtroStatus;
      if (filtroResponsavel) params.responsavel = filtroResponsavel;
      if (filtroDataInicio)  params.data_inicio = filtroDataInicio;
      if (filtroDataFim)     params.data_fim    = filtroDataFim;
      const r = await axios.get(`${API_URL}/multas`, { headers: hdr(), params });
      setMultas(Array.isArray(r.data) ? r.data : []);
    } catch { toast('Erro ao carregar multas', 'error'); }
  }, [filtroVeiculo, filtroStatus, filtroResponsavel, filtroDataInicio, filtroDataFim]);

  useEffect(() => {
    (async () => {
      setCarregando(true);
      try {
        const [rv, rc] = await Promise.all([
          axios.get(`${API_URL}/veiculos`,  { headers: hdr() }),
          axios.get(`${API_URL}/clientes`,  { headers: hdr() }),
        ]);
        setVeiculos(rv.data || []);
        setClientes(Array.isArray(rc.data) ? rc.data : []);
      } catch { toast('Erro ao carregar dados', 'error'); }
      finally { setCarregando(false); }
    })();
  }, []);

  useEffect(() => { recarregarMultas(); }, [recarregarMultas]);

  // filtro client-side para previsão de pagamento
  const multasFiltradas = useMemo(() => {
    let lista = multas;
    if (filtroPrevInicio) lista = lista.filter(m => m.previsao_pagamento >= filtroPrevInicio);
    if (filtroPrevFim)    lista = lista.filter(m => m.previsao_pagamento && m.previsao_pagamento <= filtroPrevFim);
    return lista;
  }, [multas, filtroPrevInicio, filtroPrevFim]);

  const stats = useMemo(() => ({
    total:            multasFiltradas.length,
    pendentes:        multasFiltradas.filter(m => m.status === 'pendente').length,
    pagasLocadora:    multasFiltradas.filter(m => m.status === 'pago_locadora').length,
    recebidasCliente: multasFiltradas.filter(m => m.status === 'recebido_cliente').length,
    valorTotal:       multasFiltradas.reduce((s, m) => s + parseFloat(m.valor || 0), 0),
    valorPendente:    multasFiltradas
                        .filter(m => !['recebido_cliente','cancelada'].includes(m.status))
                        .reduce((s, m) => s + parseFloat(m.valor || 0), 0),
  }), [multasFiltradas]);

  // ── DRAG ─────────────────────────────────────────────────
  const handleResizeDragStart = useCallback((e) => {
    e.preventDefault();
    const container = splitContainerRef.current;
    if (!container) return;
    const onMove = (ev) => {
      const rect = container.getBoundingClientRect();
      const ratio = ((ev.clientX - rect.left) / rect.width) * 100;
      setSplitRatio(Math.max(25, Math.min(65, ratio)));
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, []);

  // ── ABRIR PAINEL ─────────────────────────────────────────
  const abrirPainel = useCallback((opts = {}) => {
    // opts: { veiculo, multaParaEditar }
    const { veiculo = null, multaParaEditar = null } = opts;

    setConsultaResultado(null);
    setConsultaCarregando(false);

    if (multaParaEditar) {
      // modo edição
      setModoEdicao(true);
      setMultaEditId(multaParaEditar.id);
      setFormVeiculoId(multaParaEditar.veiculo_id || '');
      setFormCliente(clientes.find(c => c.id === multaParaEditar.cliente_id) || null);
      setFormState({
        auto_infracao:      multaParaEditar.auto_infracao      || '',
        situacao_detran:    multaParaEditar.situacao_detran     || '',
        orgao_emissor:      multaParaEditar.orgao_emissor       || '',
        codigo_multa:       multaParaEditar.codigo_multa        || '',
        descricao:          multaParaEditar.descricao           || '',
        data_infracao:      multaParaEditar.data_infracao       ? multaParaEditar.data_infracao.substring(0,10) : '',
        data_vencimento:    multaParaEditar.data_vencimento     ? multaParaEditar.data_vencimento.substring(0,10) : '',
        previsao_pagamento: multaParaEditar.previsao_pagamento  ? multaParaEditar.previsao_pagamento.substring(0,10) : '',
        valor:              String(multaParaEditar.valor || ''),
        responsavel:        multaParaEditar.responsavel         || 'cliente',
        observacoes:        multaParaEditar.observacoes         || '',
        observacao_cobranca: multaParaEditar.observacao_cobranca || '',
      });
      // pré-carrega consulta para o veículo da multa
      const v = veiculos.find(x => x.id === multaParaEditar.veiculo_id);
      setVeiculoConsulta(v || null);
    } else {
      // modo criação
      setModoEdicao(false);
      setMultaEditId(null);
      setFormState(FORM_VAZIO);
      setFormCliente(null);
      setFormVeiculoId(veiculo?.id || '');
      setVeiculoConsulta(veiculo || null);
    }

    setPainelAberto(true);
    setDialogDetalhes(false);
  }, [clientes, veiculos]);

  // ── CONSULTAR INFOSIMPLES ─────────────────────────────────
  const executarConsulta = useCallback(async (veiculo) => {
    const v = veiculo || veiculoConsulta;
    if (!v?.renavam || !v?.placa) return toast('Veículo sem RENAVAM ou placa', 'warning');
    setConsultaCarregando(true);
    setConsultaResultado(null);
    try {
      const r = await axios.post(
        `${API_URL}/integracoes/infosimples/consultar-multas`,
        { renavam: v.renavam, placa: v.placa },
        { headers: hdr() }
      );
      setConsultaResultado(r.data);
    } catch (err) {
      const d = err.response?.data;
      setConsultaResultado({ sucesso: false, mensagem: d?.mensagem || err.message, sem_token: d?.sem_token });
    } finally {
      setConsultaCarregando(false);
    }
  }, [veiculoConsulta]);

  // quando veículo de consulta muda → sincroniza veículo do formulário
  const handleVeiculoConsultaChange = (v) => {
    setVeiculoConsulta(v);
    if (v) setFormVeiculoId(v.id);
  };

  // ── PRÉ-PREENCHER FORMULÁRIO com multa da API ─────────────
  const usarMultaAPI = (m) => {
    const parseData = (d) => {
      if (!d) return '';
      const match = String(d).match(/(\d{2})\/(\d{2})\/(\d{4})/);
      if (match) return `${match[3]}-${match[2]}-${match[1]}`;
      if (String(d).match(/^\d{4}-\d{2}-\d{2}/)) return String(d).substring(0, 10);
      return '';
    };

    // monta observações automáticas com campos extras da API
    const extras = [];
    const local = m.local_infracao || m.local || m.endereco_infracao || m.endereco || '';
    const municipio = m.municipio || m.cidade || m.municipio_infracao || '';
    const uf = m.uf || m.estado || '';
    const pontos = m.pontos || m.qtd_pontos || '';
    const natureza = m.natureza || m.gravidade || m.classificacao || '';
    const agente = m.agente_autuador || m.agente || m.autuador_nome || '';
    if (local)     extras.push(`Local: ${local}`);
    if (municipio) extras.push(`Município: ${municipio}${uf ? `/${uf}` : ''}`);
    if (pontos)    extras.push(`Pontos: ${pontos}`);
    if (natureza)  extras.push(`Natureza: ${natureza}`);
    if (agente)    extras.push(`Agente: ${agente}`);

    setFormState(s => ({
      ...s,
      auto_infracao:   m.auto_infracao  || m.ait             || m.numero_auto     || '',
      descricao:       m.descricao      || m.descricao_infracao || m.descricao_enquadramento || m.infracao || m.enquadramento || '',
      data_infracao:   parseData(m.data_infracao || m.data_auto || m.data   || m.data_cometimento),
      data_vencimento: parseData(m.vencimento    || m.data_vencimento || m.data_limite || m.prazo_pagamento),
      valor:           String(m.valor   || m.valor_com_desconto || m.valor_multa || m.valor_total || ''),
      orgao_emissor:   m.orgao          || m.orgao_emissor    || m.autuador       || '',
      situacao_detran: m.situacao       || m.situacao_detran  || m.status_detran  || '',
      codigo_multa:    m.codigo         || m.codigo_enquadramento || m.enquadramento || '',
      observacoes:     extras.length ? extras.join(' | ') : (s.observacoes || ''),
    }));
    if (veiculoConsulta) setFormVeiculoId(veiculoConsulta.id);
    toast('Dados preenchidos! Complete o formulário e salve.', 'info');
  };

  // ── SALVAR MULTA ─────────────────────────────────────────
  const salvarMulta = async () => {
    if (!formVeiculoId) return toast('Selecione um veículo', 'warning');
    if (!formCliente)   return toast('Selecione o condutor responsável', 'warning');

    const payload = {
      ...form,
      valor: parseFloat(form.valor) || 0,
      veiculo_id: formVeiculoId,
      cliente_id: formCliente.id,
    };

    try {
      if (modoEdicao) {
        await axios.put(`${API_URL}/multas/${multaEditId}`, payload, { headers: hdr() });
        toast('Multa atualizada com sucesso!');
      } else {
        await axios.post(`${API_URL}/multas`, payload, { headers: hdr() });
        toast('Multa registrada com sucesso!');
      }
      setPainelAberto(false);
      recarregarMultas();
    } catch (err) {
      toast(err.response?.data?.erro || 'Erro ao salvar multa', 'error');
    }
  };

  // ── ATUALIZAR STATUS ──────────────────────────────────────
  const atualizarStatus = async (novoStatus) => {
    try {
      await axios.patch(`${API_URL}/multas/${multaSelecionada.id}/status`, { status: novoStatus }, { headers: hdr() });
      toast('Status atualizado!');
      setMultaSelecionada(s => ({ ...s, status: novoStatus }));
      recarregarMultas();
    } catch (err) {
      toast(err.response?.data?.erro || 'Erro ao atualizar status', 'error');
    }
  };

  // ── ALTERAR CONDUTOR ──────────────────────────────────────
  const salvarCondutor = async () => {
    if (!dialogCondutor.novoCliente) return toast('Selecione o novo condutor', 'warning');
    try {
      await axios.patch(
        `${API_URL}/multas/${multaSelecionada.id}/condutor`,
        { cliente_id: dialogCondutor.novoCliente.id, observacao_cobranca: dialogCondutor.obs || null },
        { headers: hdr() }
      );
      toast('Condutor alterado com sucesso!');
      setDialogCondutor({ aberto: false, novoCliente: null, obs: '' });
      setMultaSelecionada(s => ({ ...s, cliente_nome: dialogCondutor.novoCliente.nome, cliente_id: dialogCondutor.novoCliente.id }));
      recarregarMultas();
    } catch (err) {
      toast(err.response?.data?.erro || 'Erro ao alterar condutor', 'error');
    }
  };

  // ── EXCLUIR ───────────────────────────────────────────────
  const excluirMulta = async () => {
    try {
      await axios.delete(`${API_URL}/multas/${dialogExclusao.multa.id}`, { headers: hdr() });
      toast('Multa excluída!');
      setDialogExclusao({ aberto: false, multa: null });
      setDialogDetalhes(false);
      recarregarMultas();
    } catch { toast('Erro ao excluir', 'error'); }
  };

  // ── COLUNAS DA TABELA ─────────────────────────────────────
  const colunas = useMemo(() => [
    {
      accessorKey: 'auto_infracao', header: 'Auto Infração', size: 120,
      Cell: ({ cell }) => cell.getValue() || <Typography variant="caption" color="text.secondary">—</Typography>,
    },
    {
      accessorKey: 'veiculo_placa', header: 'Veículo', size: 110,
      Cell: ({ row }) => (
        <Box>
          <Typography variant="body2" fontWeight="bold">{row.original.veiculo_placa}</Typography>
          <Typography variant="caption" color="text.secondary">{row.original.veiculo_modelo}</Typography>
        </Box>
      ),
    },
    {
      accessorKey: 'descricao', header: 'Descrição', size: 200,
      Cell: ({ cell }) => (
        <Tooltip title={cell.getValue() || ''}>
          <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>{cell.getValue() || '—'}</Typography>
        </Tooltip>
      ),
    },
    {
      accessorKey: 'cliente_nome', header: 'Condutor', size: 150,
      Cell: ({ cell }) => cell.getValue() || <Typography variant="caption" color="text.secondary">—</Typography>,
    },
    {
      accessorKey: 'data_infracao', header: 'Infração', size: 95,
      Cell: ({ cell }) => fmtData(cell.getValue()),
    },
    {
      accessorKey: 'previsao_pagamento', header: 'Prev. Pgto', size: 105,
      Cell: ({ cell }) => cell.getValue()
        ? <Chip label={fmtData(cell.getValue())} size="small" color="info" variant="outlined" />
        : <Typography variant="caption" color="text.secondary">—</Typography>,
    },
    {
      accessorKey: 'valor', header: 'Valor', size: 100,
      Cell: ({ cell }) => (
        <Typography fontWeight="bold" color="error.main" variant="body2">{fmtValor(cell.getValue())}</Typography>
      ),
    },
    {
      accessorKey: 'status', header: 'Status', size: 155,
      Cell: ({ cell }) => {
        const info = STATUS_MAP[cell.getValue()] || STATUS_MAP.pendente;
        return <Chip label={info.label} color={info.color} size="small" />;
      },
    },
    {
      id: 'acoes', header: 'Ações', size: 90, enableSorting: false,
      Cell: ({ row }) => (
        <Box display="flex" gap={0.5}>
          <Tooltip title="Detalhes / status">
            <IconButton size="small" color="primary" onClick={() => { setMultaSelecionada(row.original); setDialogDetalhes(true); }}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Excluir">
            <IconButton size="small" color="error" onClick={() => setDialogExclusao({ aberto: true, multa: row.original })}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ], []);

  // ── RENDER RESULTADOS INFOSIMPLES ─────────────────────────
  const renderResultados = () => {
    if (consultaCarregando) return (
      <Box display="flex" flexDirection="column" alignItems="center" py={6} gap={2} sx={{ textAlign: 'center', px: 3 }}>
        <CircularProgress size={44} />
        <Typography color="text.primary" variant="body1" fontWeight="bold">
          Consultando DETRAN-AM…
        </Typography>
        <Typography color="text.secondary" variant="body2">
          O sistema governamental pode demorar até 2–3 minutos para responder.
          Aguarde sem fechar esta janela.
        </Typography>
      </Box>
    );

    if (!consultaResultado) return (
      <Box display="flex" flexDirection="column" alignItems="center" py={8} gap={1} sx={{ color: 'text.secondary' }}>
        <SearchIcon sx={{ fontSize: 48, opacity: 0.3 }} />
        <Typography variant="body2">Selecione um veículo e clique em Consultar</Typography>
      </Box>
    );

    if (!consultaResultado.sucesso) {
      const codigo = consultaResultado.codigo;
      const info = {
        603: { sev: 'warning', msg: 'Este serviço não está habilitado na sua conta Infosimples. Solicite acesso em infosimples.com.' },
        604: { sev: 'warning', msg: 'Saldo de créditos esgotado. Acesse infosimples.com para recarregar.' },
        600: { sev: 'warning', msg: 'Erro inesperado no servidor Infosimples ou DETRAN-AM. Aguarde alguns minutos e tente novamente.', retry: true },
        605: { sev: 'warning', msg: 'O DETRAN-AM demorou demais para responder. O sistema governamental às vezes fica lento. Aguarde alguns minutos e clique em "Consultar novamente".', retry: true },
        610: { sev: 'warning', msg: 'O site do DETRAN-AM está temporariamente indisponível ou com CAPTCHA. Tente novamente mais tarde.', retry: true },
        401: { sev: 'error',   msg: 'Token inválido. Verifique o token em Integrações → Infosimples.' },
        402: { sev: 'error',   msg: 'Token expirado. Gere um novo token em infosimples.com.' },
      }[codigo] || { sev: consultaResultado.sem_token ? 'warning' : 'error', msg: consultaResultado.mensagem, retry: true };

      return (
        <Box sx={{ m: 2 }}>
          <Alert severity={info.sev}>
            <Typography fontWeight="bold">
              {consultaResultado.sem_token
                ? 'Infosimples não configurado'
                : codigo ? `Erro ${codigo}` : 'Falha na consulta'}
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>{info.msg}</Typography>
          </Alert>
          {info.retry && (
            <Button
              fullWidth variant="outlined" startIcon={<RefreshIcon />}
              onClick={() => executarConsulta(veiculoConsulta)}
              sx={{ mt: 1.5 }}
            >
              Consultar novamente
            </Button>
          )}
        </Box>
      );
    }

    const dataItems = consultaResultado.multas || [];
    const item      = dataItems[0] || {};
    const lista     = Array.isArray(item.multas) ? item.multas : (dataItems.length > 0 && !item.multas ? dataItems : []);
    const siteLink  = item['site receipt'] || item.site_receipt || null;
    const dadosVei  = item.dados && typeof item.dados === 'object' ? item.dados : null;

    if (lista.length === 0) return (
      <Box p={2}>
        {dadosVei && <Alert severity="info" sx={{ mb: 2, fontSize: 12 }}>
          {Object.entries(dadosVei).slice(0, 6).map(([k, v]) => (
            <span key={k} style={{ marginRight: 12 }}><strong>{k.replace(/_/g,' ')}:</strong> {String(v ?? '')}</span>
          ))}
        </Alert>}
        <Alert severity="success">Nenhuma multa encontrada para este veículo.</Alert>
      </Box>
    );

    return (
      <Box>
        {dadosVei && (
          <Box sx={{ px: 2, pt: 2, pb: 1 }}>
            <Alert severity="info" sx={{ fontSize: 11 }}>
              {Object.entries(dadosVei).slice(0, 6).map(([k, v]) => (
                <span key={k} style={{ marginRight: 12 }}><strong>{k.replace(/_/g,' ')}:</strong> {String(v ?? '')}</span>
              ))}
            </Alert>
          </Box>
        )}
        <Box sx={{ px: 2, pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="subtitle2" color="text.secondary">
            {lista.length} multa(s) encontrada(s)
          </Typography>
          {siteLink && (
            <Button size="small" component="a" href={siteLink} target="_blank" rel="noopener noreferrer">
              Ver site DETRAN
            </Button>
          )}
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, px: 2, pb: 2 }}>
          {lista.map((m, i) => {
            const auto     = m.auto_infracao || m.ait || m.numero_auto || `Multa ${i + 1}`;
            const desc     = m.descricao || m.descricao_infracao || m.descricao_enquadramento || m.infracao || m.enquadramento || '—';
            const valStr   = m.valor || m.valor_com_desconto || m.valor_multa || m.valor_total;
            const data     = m.data_infracao || m.data_auto || m.data || m.data_cometimento;
            const venc     = m.vencimento || m.data_vencimento || m.data_limite || m.prazo_pagamento;
            const sit      = m.situacao || m.situacao_detran || m.status_detran;
            const local    = m.local_infracao || m.local || m.endereco_infracao || '';
            const municipio = m.municipio || m.cidade || '';
            const uf       = m.uf || m.estado || '';
            const pontos   = m.pontos || m.qtd_pontos || '';
            const natureza = m.natureza || m.gravidade || m.classificacao || '';
            const codigo   = m.codigo || m.codigo_enquadramento || '';
            const orgao    = m.orgao || m.orgao_emissor || '';

            return (
              <Card key={i} variant="outlined" sx={{
                borderRadius: 2, borderColor: '#ddd',
                '&:hover': { borderColor: '#ed3237', boxShadow: '0 2px 8px rgba(247,139,0,0.15)' },
                transition: 'all 0.15s',
              }}>
                <CardContent sx={{ pb: 1, pt: 1.5, px: 2 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={0.5}>
                    <Typography variant="subtitle2" fontWeight="bold" color="text.primary">
                      {auto}
                    </Typography>
                    {valStr && (
                      <Typography variant="subtitle2" fontWeight="bold" color="error.main">
                        {fmtValor(valStr)}
                      </Typography>
                    )}
                  </Box>

                  {/* Descrição da infração */}
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.8, lineHeight: 1.4, fontStyle: 'italic' }}>
                    {desc}
                  </Typography>

                  {/* Chips principais */}
                  <Box display="flex" gap={0.8} flexWrap="wrap" mb={0.5}>
                    {data     && <Chip label={`Infração: ${data}`}  size="small" variant="outlined" />}
                    {venc     && <Chip label={`Vence: ${venc}`}     size="small" variant="outlined" color="warning" />}
                    {sit      && <Chip label={sit}                  size="small" color="info" />}
                    {natureza && <Chip label={natureza}             size="small" color="error" variant="outlined" />}
                    {pontos   && <Chip label={`${pontos} pts`}      size="small" color="default" variant="outlined" />}
                  </Box>

                  {/* Informações extras */}
                  {(local || municipio || codigo || orgao) && (
                    <Box sx={{ mt: 0.5, pt: 0.5, borderTop: '1px solid #f0f0f0' }}>
                      {(local || municipio) && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          📍 {[local, municipio && `${municipio}${uf ? `/${uf}` : ''}`].filter(Boolean).join(' — ')}
                        </Typography>
                      )}
                      {(codigo || orgao) && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {codigo && `Código: ${codigo}`}{codigo && orgao ? ' · ' : ''}{orgao && `Órgão: ${orgao}`}
                        </Typography>
                      )}
                    </Box>
                  )}
                </CardContent>
                <CardActions sx={{ px: 2, pt: 0, pb: 1 }}>
                  <Button
                    size="small"
                    variant="contained"
                    endIcon={<ArrowForwardIcon />}
                    onClick={() => usarMultaAPI(m)}
                    sx={{ bgcolor: '#ed3237', '&:hover': { bgcolor: '#c41e2b' }, fontSize: 12 }}
                  >
                    Usar no formulário →
                  </Button>
                </CardActions>
              </Card>
            );
          })}
        </Box>
      </Box>
    );
  };

  // ─────────────────────────────────────────────────────────────
  // JSX PRINCIPAL
  // ─────────────────────────────────────────────────────────────
  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>

      {/* ── HEADER ── */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 3, boxShadow: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2}>
          <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#ed3237' }}>
            🚦 Gestão de Multas
          </Typography>
          <Box display="flex" gap={1} flexWrap="wrap">
            <Button variant="outlined" startIcon={<FilterListIcon />} onClick={() => setFiltrosVisiveis(v => !v)} size="small">
              Filtros
            </Button>
            <Button
              variant="outlined"
              startIcon={<SearchIcon />}
              onClick={() => {
                const v = veiculos.find(x => x.id === filtroVeiculo);
                abrirPainel({ veiculo: v || null });
              }}
              sx={{ borderColor: '#1976d2', color: '#1976d2' }}
            >
              Consultar Infosimples
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => abrirPainel()}
              sx={{ bgcolor: '#ed3237', '&:hover': { bgcolor: '#c41e2b' } }}
            >
              Registrar Multa
            </Button>
          </Box>
        </Box>

        {filtrosVisiveis && (
          <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #eee' }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={3}>
                <Autocomplete
                  options={veiculos}
                  getOptionLabel={o => o ? `${o.placa} — ${o.modelo}` : ''}
                  value={veiculos.find(v => v.id === filtroVeiculo) || null}
                  onChange={(_, v) => setFiltroVeiculo(v?.id || '')}
                  renderInput={p => <TextField {...p} label="Veículo" size="small" />}
                  isOptionEqualToValue={(o, v) => o.id === v.id}
                  noOptionsText="Nenhum"
                />
              </Grid>
              <Grid item xs={6} sm={3} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select value={filtroStatus} label="Status" onChange={e => setFiltroStatus(e.target.value)}>
                    <MenuItem value="">Todos</MenuItem>
                    {Object.entries(STATUS_MAP).map(([k, v]) => <MenuItem key={k} value={k}>{v.label}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} sm={3} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Responsável</InputLabel>
                  <Select value={filtroResponsavel} label="Responsável" onChange={e => setFiltroResponsavel(e.target.value)}>
                    <MenuItem value="">Todos</MenuItem>
                    <MenuItem value="cliente">Cliente</MenuItem>
                    <MenuItem value="locadora">Locadora</MenuItem>
                    <MenuItem value="indefinido">A definir</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} sm={3} md={1.5}>
                <TextField fullWidth size="small" label="Infração de" type="date"
                  value={filtroDataInicio} onChange={e => setFiltroDataInicio(e.target.value)}
                  InputLabelProps={{ shrink: true }} />
              </Grid>
              <Grid item xs={6} sm={3} md={1.5}>
                <TextField fullWidth size="small" label="Infração até" type="date"
                  value={filtroDataFim} onChange={e => setFiltroDataFim(e.target.value)}
                  InputLabelProps={{ shrink: true }} />
              </Grid>
              <Grid item xs={6} sm={3} md={1}>
                <TextField fullWidth size="small" label="Prev. de" type="date"
                  value={filtroPrevInicio} onChange={e => setFiltroPrevInicio(e.target.value)}
                  InputLabelProps={{ shrink: true }} />
              </Grid>
              <Grid item xs={6} sm={3} md={1}>
                <TextField fullWidth size="small" label="Prev. até" type="date"
                  value={filtroPrevFim} onChange={e => setFiltroPrevFim(e.target.value)}
                  InputLabelProps={{ shrink: true }} />
              </Grid>
              <Grid item xs="auto">
                <Button size="small" onClick={() => {
                  setFiltroVeiculo(''); setFiltroStatus(''); setFiltroResponsavel('');
                  setFiltroDataInicio(''); setFiltroDataFim('');
                  setFiltroPrevInicio(''); setFiltroPrevFim('');
                }}>Limpar</Button>
              </Grid>
            </Grid>
          </Box>
        )}
      </Paper>

      {/* ── CARDS ── */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Total',             value: stats.total,                         icon: <ReceiptLongIcon sx={{ fontSize: 32, color: '#ed3237' }} />, color: '#ed3237', bg: '#fff3e0' },
          { label: 'Pendentes',         value: stats.pendentes,                     icon: <PendingIcon      sx={{ fontSize: 32, color: '#ff9800' }} />, color: '#ff9800', bg: '#fff8e1' },
          { label: 'Pagas Locadora',    value: stats.pagasLocadora,                 icon: <PaidIcon         sx={{ fontSize: 32, color: '#1976d2' }} />, color: '#1976d2', bg: '#e3f2fd' },
          { label: 'Recebidas Cliente', value: stats.recebidasCliente,              icon: <AssignmentTurnedInIcon sx={{ fontSize: 32, color: '#2e7d32' }} />, color: '#2e7d32', bg: '#e8f5e9' },
          { label: 'Valor Pendente',    value: fmtValor(stats.valorPendente),       icon: <WarningIcon      sx={{ fontSize: 32, color: '#c62828' }} />, color: '#c62828', bg: '#fce4ec', grande: true },
          { label: 'Valor Total',       value: fmtValor(stats.valorTotal),          icon: <AccountBalanceWalletIcon sx={{ fontSize: 32, color: '#6a1b9a' }} />, color: '#6a1b9a', bg: '#f3e5f5', grande: true },
        ].map((c, i) => (
          <Grid item xs={12} sm={6} md={4} lg={2} key={i}>
            <Card sx={{ bgcolor: c.bg, borderLeft: `4px solid ${c.color}`, borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', height: '100%' }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', fontSize: 10 }}>
                    {c.label}
                  </Typography>
                  {c.icon}
                </Box>
                <Typography variant={c.grande ? 'h6' : 'h4'} fontWeight="bold" color={c.color} sx={{ lineHeight: 1 }}>
                  {c.value}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* ── TABELA ── */}
      <Paper sx={{ borderRadius: 3, boxShadow: 3, overflow: 'hidden' }}>
        <MaterialReactTable
          columns={colunas}
          data={multasFiltradas}
          state={{ isLoading: carregando }}
          localization={MRT_Localization_PT_BR}
          enableColumnFilters
          enableSorting
          enablePagination
          initialState={{ density: 'compact' }}
          muiTableContainerProps={{ sx: { maxHeight: '520px' } }}
          renderEmptyRowsFallback={() => (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography color="text.secondary">Nenhuma multa encontrada.</Typography>
            </Box>
          )}
        />
      </Paper>

      {/* ══════════════════════════════════════════════════════════
          PAINEL SPLIT VIEW — Consultar + Registrar
      ══════════════════════════════════════════════════════════ */}
      {painelAberto && (
        <>
          {/* Backdrop */}
          <Box onClick={() => setPainelAberto(false)}
            sx={{ position: 'fixed', inset: 0, bgcolor: 'rgba(0,0,0,0.55)', zIndex: 1200 }} />

          {/* Painel principal */}
          <Box sx={{
            position: 'fixed',
            top: '1.5%',
            left: '50%',
            transform: 'translateX(-50%)',
            width: { xs: '99vw', md: '94vw', lg: '90vw' },
            height: '97vh',
            bgcolor: 'background.paper',
            borderRadius: 3,
            boxShadow: '0 8px 40px rgba(0,0,0,0.35)',
            display: 'flex',
            flexDirection: 'column',
            zIndex: 1300,
            overflow: 'hidden',
          }}>

            {/* Barra de título */}
            <Box sx={{
              px: 2.5, py: 1.5,
              bgcolor: '#1a1a2e', color: 'white',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              flexShrink: 0,
            }}>
              <Box display="flex" alignItems="center" gap={2}>
                <Typography variant="h6" fontWeight="bold">
                  {modoEdicao ? '✏️ Editar Multa' : '📋 Consultar e Registrar Multa'}
                </Typography>
                {veiculoConsulta && (
                  <>
                    <Chip
                      size="small" label={veiculoConsulta.placa}
                      icon={<ContentCopyIcon sx={{ fontSize: '11px !important' }} />}
                      onClick={() => { navigator.clipboard.writeText(veiculoConsulta.placa); toast('Placa copiada!'); }}
                      sx={{ cursor: 'pointer', bgcolor: 'rgba(255,255,255,0.15)', color: 'white', fontFamily: 'monospace', fontWeight: 'bold',
                            '& .MuiChip-icon': { color: 'white' } }}
                    />
                    {veiculoConsulta.renavam && (
                      <Chip
                        size="small" label={`RENAVAM: ${veiculoConsulta.renavam}`}
                        icon={<ContentCopyIcon sx={{ fontSize: '11px !important' }} />}
                        onClick={() => { navigator.clipboard.writeText(veiculoConsulta.renavam); toast('RENAVAM copiado!'); }}
                        sx={{ cursor: 'pointer', bgcolor: 'rgba(255,255,255,0.12)', color: 'white', fontFamily: 'monospace',
                              '& .MuiChip-icon': { color: 'white' } }}
                      />
                    )}
                  </>
                )}
              </Box>
              <IconButton onClick={() => setPainelAberto(false)} sx={{ color: 'white' }}>
                <CloseIcon />
              </IconButton>
            </Box>

            {/* Corpo split */}
            <Box ref={splitContainerRef} sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

              {/* ── LADO ESQUERDO: Consulta ── */}
              <Box sx={{
                width: `${splitRatio}%`,
                flexShrink: 0,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                borderRight: '1px solid #e0e0e0',
              }}>
                {/* Toolbar da consulta */}
                <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderBottom: '1px solid #e0e0e0', flexShrink: 0 }}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5, fontWeight: 'bold', textTransform: 'uppercase', fontSize: 11 }}>
                    🔍 Consulta Infosimples — DETRAN-AM
                  </Typography>
                  <Autocomplete
                    options={veiculos}
                    getOptionLabel={o => o ? `${o.placa} — ${o.modelo}` : ''}
                    value={veiculoConsulta}
                    onChange={(_, v) => handleVeiculoConsultaChange(v)}
                    renderInput={p => <TextField {...p} label="Selecione o veículo para consultar" size="small" />}
                    isOptionEqualToValue={(o, v) => o?.id === v?.id}
                    renderOption={(props, option) => {
                      const { key, ...rest } = props;
                      return (
                        <li key={option.id} {...rest}>
                          <Box>
                            <Typography variant="body2" fontWeight="bold">{option.placa}</Typography>
                            <Typography variant="caption" color="text.secondary">{option.modelo} — RENAVAM: {option.renavam}</Typography>
                          </Box>
                        </li>
                      );
                    }}
                    noOptionsText="Nenhum veículo"
                    sx={{ mb: 1.5 }}
                  />
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={consultaCarregando ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
                    onClick={() => executarConsulta(veiculoConsulta)}
                    disabled={!veiculoConsulta || consultaCarregando}
                    sx={{ bgcolor: '#1a1a2e', '&:hover': { bgcolor: '#2d2d4e' } }}
                  >
                    {consultaCarregando ? 'Consultando…' : 'Consultar'}
                  </Button>
                  {consultaResultado && (
                    <Button
                      fullWidth size="small" variant="text" startIcon={<RefreshIcon />}
                      onClick={() => executarConsulta(veiculoConsulta)}
                      disabled={consultaCarregando}
                      sx={{ mt: 0.5 }}
                    >
                      Consultar novamente
                    </Button>
                  )}
                </Box>

                {/* Resultados */}
                <Box sx={{ flex: 1, overflowY: 'auto' }}>
                  {renderResultados()}
                </Box>
              </Box>

              {/* ── DRAG HANDLE ── */}
              <Box
                onMouseDown={handleResizeDragStart}
                sx={{
                  width: 6, flexShrink: 0, cursor: 'col-resize',
                  bgcolor: '#1a1a2e',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  transition: 'background-color 0.15s',
                  '&:hover': { bgcolor: '#ed3237' },
                  '&::after': { content: '""', display: 'block', width: 2, height: 40, bgcolor: 'rgba(255,255,255,0.3)', borderRadius: 1 },
                }}
              />

              {/* ── LADO DIREITO: Formulário ── */}
              <Box sx={{ flex: 1, overflowY: 'auto', p: 3, minWidth: 0 }}>

                <Typography variant="h6" fontWeight="bold" sx={{ mb: 3, color: '#1a1a2e' }}>
                  {modoEdicao ? 'Editar dados da multa' : 'Registrar nova multa'}
                </Typography>

                {/* Veículo (formulário) */}
                <Paper variant="outlined" sx={{ p: 2.5, mb: 3, borderRadius: 2 }}>
                  <Typography variant="subtitle1" fontWeight="bold" color="text.secondary" sx={{ mb: 2, textTransform: 'uppercase', fontSize: 12, letterSpacing: 0.5 }}>
                    Veículo
                  </Typography>
                  <Autocomplete
                    options={veiculos}
                    getOptionLabel={o => o ? `${o.placa} — ${o.modelo}` : ''}
                    value={veiculos.find(v => v.id === formVeiculoId) || null}
                    onChange={(_, v) => setFormVeiculoId(v?.id || '')}
                    renderInput={p => <TextField {...p} label="Selecione o veículo *" />}
                    isOptionEqualToValue={(o, v) => o?.id === v?.id}
                    noOptionsText="Nenhum veículo"
                  />
                </Paper>

                {/* Dados da multa */}
                <Paper variant="outlined" sx={{ p: 2.5, mb: 3, borderRadius: 2 }}>
                  <Typography variant="subtitle1" fontWeight="bold" color="text.secondary" sx={{ mb: 2.5, textTransform: 'uppercase', fontSize: 12, letterSpacing: 0.5 }}>
                    Dados da Multa
                  </Typography>
                  <Grid container spacing={2.5}>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="Auto de Infração" placeholder="Ex: MT02097629"
                        value={form.auto_infracao} onChange={e => setField('auto_infracao', e.target.value)} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Autocomplete freeSolo
                        options={['Notificação de Autuação', 'Notificação de Imposição de Penalidade', 'Multa Paga']}
                        value={form.situacao_detran}
                        onChange={(_, v) => setField('situacao_detran', v || '')}
                        onInputChange={(_, v) => setField('situacao_detran', v)}
                        renderInput={p => <TextField {...p} label="Situação DETRAN" placeholder="Digite ou selecione" />}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="Órgão Emissor" placeholder="Ex: 202550/AM"
                        value={form.orgao_emissor} onChange={e => setField('orgao_emissor', e.target.value)} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="Código da Multa" placeholder="Ex: 7633-1"
                        value={form.codigo_multa} onChange={e => setField('codigo_multa', e.target.value)} />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField fullWidth label="Descrição da Infração" multiline rows={2}
                        placeholder="Ex: DIRIGIR VEÍCULO SEGURANDO TELEFONE CELULAR"
                        value={form.descricao} onChange={e => setField('descricao', e.target.value)} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField fullWidth label="Data da Infração" type="date"
                        value={form.data_infracao} onChange={e => setField('data_infracao', e.target.value)}
                        InputLabelProps={{ shrink: true }} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField fullWidth label="Data de Vencimento" type="date"
                        value={form.data_vencimento} onChange={e => setField('data_vencimento', e.target.value)}
                        InputLabelProps={{ shrink: true }} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField fullWidth label="Valor da Multa"
                        value={form.valor} onChange={e => setField('valor', e.target.value)}
                        placeholder="0,00"
                        InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }} />
                    </Grid>
                  </Grid>
                </Paper>

                {/* Condutor */}
                <Paper variant="outlined" sx={{ p: 2.5, mb: 3, borderRadius: 2, borderColor: '#ed3237' }}>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2.5, textTransform: 'uppercase', fontSize: 12, letterSpacing: 0.5, color: '#ed3237' }}>
                    Condutor Responsável *
                  </Typography>
                  <Autocomplete
                    options={clientes}
                    getOptionLabel={o => (!o || typeof o !== 'object') ? '' : o.nome || ''}
                    value={formCliente}
                    onChange={(_, v) => setFormCliente(v)}
                    renderInput={p => (
                      <TextField {...p} label="Nome do condutor *"
                        helperText={`${clientes.length} clientes cadastrados`} />
                    )}
                    renderOption={(props, option) => {
                      const { key, ...rest } = props;
                      return (
                        <li key={option.id} {...rest}>
                          <Box sx={{ py: 0.5 }}>
                            <Typography variant="body2" fontWeight="bold">{option.nome}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {option.cpf_cnpj || 'Sem CPF'} · {option.telefone || 'Sem telefone'}
                            </Typography>
                          </Box>
                        </li>
                      );
                    }}
                    isOptionEqualToValue={(o, v) => o?.id === v?.id}
                    filterOptions={(opts, { inputValue }) => {
                      if (!inputValue) return opts;
                      const s = inputValue.toLowerCase();
                      return opts.filter(o => o.nome?.toLowerCase().includes(s) || o.cpf_cnpj?.includes(s) || o.telefone?.includes(s));
                    }}
                    noOptionsText="Nenhum cliente encontrado"
                  />
                </Paper>

                {/* Cobrança */}
                <Paper variant="outlined" sx={{ p: 2.5, mb: 3, borderRadius: 2 }}>
                  <Typography variant="subtitle1" fontWeight="bold" color="text.secondary" sx={{ mb: 2.5, textTransform: 'uppercase', fontSize: 12, letterSpacing: 0.5 }}>
                    Cobrança e Pagamento
                  </Typography>
                  <Grid container spacing={2.5}>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="Previsão de Pagamento" type="date"
                        value={form.previsao_pagamento} onChange={e => setField('previsao_pagamento', e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        helperText="Data combinada com o cliente para pagar" />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Responsável pelo pagamento</Typography>
                      <RadioGroup row value={form.responsavel} onChange={e => setField('responsavel', e.target.value)}>
                        <FormControlLabel value="cliente"    control={<Radio />} label="Cliente" />
                        <FormControlLabel value="locadora"   control={<Radio />} label="Locadora" />
                        <FormControlLabel value="indefinido" control={<Radio />} label="A definir" />
                      </RadioGroup>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="Observações gerais" multiline rows={3}
                        placeholder="Informações sobre a multa…"
                        value={form.observacoes} onChange={e => setField('observacoes', e.target.value)} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="Observação de cobrança (interno)" multiline rows={3}
                        placeholder="Notas sobre negociação, prazo solicitado pelo cliente…"
                        value={form.observacao_cobranca} onChange={e => setField('observacao_cobranca', e.target.value)}
                        helperText="Visível apenas para a equipe interna" />
                    </Grid>
                  </Grid>
                </Paper>

                {/* Botões */}
                <Box display="flex" gap={2} justifyContent="flex-end" sx={{ pb: 1 }}>
                  <Button variant="outlined" size="large" onClick={() => setPainelAberto(false)}>
                    Cancelar
                  </Button>
                  <Button
                    variant="contained" size="large" onClick={salvarMulta}
                    sx={{ bgcolor: '#ed3237', '&:hover': { bgcolor: '#c41e2b' }, px: 4 }}
                  >
                    {modoEdicao ? 'Salvar Alterações' : 'Registrar Multa'}
                  </Button>
                </Box>
              </Box>
            </Box>
          </Box>
        </>
      )}

      {/* ══════════════════════════════════════════════════════════
          DIALOG — Detalhes / Status
      ══════════════════════════════════════════════════════════ */}
      <Dialog open={dialogDetalhes} onClose={() => setDialogDetalhes(false)} maxWidth="sm" fullWidth>
        {multaSelecionada && (
          <>
            <DialogTitle sx={{ bgcolor: '#1976d2', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              📄 {multaSelecionada.auto_infracao || 'Multa sem auto'}
              <IconButton onClick={() => setDialogDetalhes(false)} sx={{ color: 'white' }}><CloseIcon /></IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: 3 }}>
              <Grid container spacing={1.5}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Veículo</Typography>
                  <Typography fontWeight="bold">{multaSelecionada.veiculo_placa} — {multaSelecionada.veiculo_modelo}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Condutor</Typography>
                  <Typography fontWeight="bold">{multaSelecionada.cliente_nome || 'Não identificado'}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">Descrição</Typography>
                  <Typography>{multaSelecionada.descricao || '—'}</Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="caption" color="text.secondary">Data Infração</Typography>
                  <Typography>{fmtData(multaSelecionada.data_infracao)}</Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="caption" color="text.secondary">Vencimento</Typography>
                  <Typography>{fmtData(multaSelecionada.data_vencimento)}</Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="caption" color="text.secondary">Prev. Pagamento</Typography>
                  <Typography color={multaSelecionada.previsao_pagamento ? 'info.main' : 'text.secondary'} fontWeight="bold">
                    {fmtData(multaSelecionada.previsao_pagamento)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Valor</Typography>
                  <Typography fontWeight="bold" color="error.main">{fmtValor(multaSelecionada.valor)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Responsável</Typography>
                  <Typography>{multaSelecionada.responsavel || '—'}</Typography>
                </Grid>
                {multaSelecionada.observacoes && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">Observações</Typography>
                    <Typography variant="body2">{multaSelecionada.observacoes}</Typography>
                  </Grid>
                )}
                {multaSelecionada.observacao_cobranca && (
                  <Grid item xs={12}>
                    <Alert severity="info" sx={{ py: 0.5 }}>
                      <Typography variant="caption" fontWeight="bold">Observação de cobrança (interno)</Typography>
                      <Typography variant="body2">{multaSelecionada.observacao_cobranca}</Typography>
                    </Alert>
                  </Grid>
                )}
              </Grid>

              <Divider sx={{ my: 2 }} />

              <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                <Typography variant="subtitle2" color="text.secondary">Status atual:</Typography>
                <Chip
                  label={(STATUS_MAP[multaSelecionada.status] || STATUS_MAP.pendente).label}
                  color={(STATUS_MAP[multaSelecionada.status] || STATUS_MAP.pendente).color}
                />
              </Box>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Alterar status:</Typography>
              <Box display="flex" flexWrap="wrap" gap={0.8}>
                {Object.entries(STATUS_MAP).map(([k, v]) => (
                  <Button key={k} size="small"
                    variant={multaSelecionada.status === k ? 'contained' : 'outlined'}
                    color={v.color === 'default' ? 'inherit' : v.color}
                    onClick={() => atualizarStatus(k)}
                  >
                    {v.label}
                  </Button>
                ))}
              </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2, display: 'flex', justifyContent: 'space-between' }}>
              <Box display="flex" gap={1}>
                <Button variant="outlined" color="secondary" startIcon={<SwapHorizIcon />}
                  onClick={() => setDialogCondutor({ aberto: true, novoCliente: null, obs: '' })}>
                  Alterar Condutor
                </Button>
                <Button variant="outlined" startIcon={<EditIcon />}
                  onClick={() => abrirPainel({ multaParaEditar: multaSelecionada })}>
                  Editar
                </Button>
              </Box>
              <Button variant="outlined" color="error" startIcon={<DeleteIcon />}
                onClick={() => setDialogExclusao({ aberto: true, multa: multaSelecionada })}>
                Excluir
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* ── DIALOG Alterar Condutor ── */}
      <Dialog open={dialogCondutor.aberto} onClose={() => setDialogCondutor({ aberto: false, novoCliente: null, obs: '' })} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box display="flex" alignItems="center" gap={1}>
            <SwapHorizIcon color="secondary" /> Alterar Condutor Responsável
          </Box>
          <IconButton onClick={() => setDialogCondutor({ aberto: false, novoCliente: null, obs: '' })}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            Condutor atual: <strong>{multaSelecionada?.cliente_nome || '—'}</strong>
          </Alert>
          <Autocomplete
            fullWidth options={clientes}
            getOptionLabel={o => (!o || typeof o !== 'object') ? '' : o.nome || ''}
            value={dialogCondutor.novoCliente}
            onChange={(_, v) => setDialogCondutor(s => ({ ...s, novoCliente: v }))}
            renderInput={p => <TextField {...p} label="Novo condutor *" />}
            renderOption={(props, option) => {
              const { key, ...rest } = props;
              return (
                <li key={option.id} {...rest}>
                  <Box sx={{ py: 0.5 }}>
                    <Typography variant="body2" fontWeight="bold">{option.nome}</Typography>
                    <Typography variant="caption" color="text.secondary">{option.cpf_cnpj}</Typography>
                  </Box>
                </li>
              );
            }}
            isOptionEqualToValue={(o, v) => o?.id === v?.id}
            filterOptions={(opts, { inputValue }) => {
              if (!inputValue) return opts;
              const s = inputValue.toLowerCase();
              return opts.filter(o => o.nome?.toLowerCase().includes(s) || o.cpf_cnpj?.includes(s));
            }}
            noOptionsText="Nenhum cliente encontrado"
            sx={{ mb: 2 }}
          />
          <TextField fullWidth multiline rows={2} size="small"
            label="Motivo / Observação (opcional)"
            placeholder="Ex: erro operacional no lançamento, condutor corrigido conforme…"
            value={dialogCondutor.obs}
            onChange={e => setDialogCondutor(s => ({ ...s, obs: e.target.value }))}
            helperText="Salvo no campo de observação de cobrança" />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogCondutor({ aberto: false, novoCliente: null, obs: '' })}>Cancelar</Button>
          <Button variant="contained" color="secondary" startIcon={<PersonIcon />} onClick={salvarCondutor}>
            Confirmar Alteração
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── DIALOG Confirmar Exclusão ── */}
      <Dialog open={dialogExclusao.aberto} onClose={() => setDialogExclusao({ aberto: false, multa: null })} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
          <DeleteIcon /> Confirmar Exclusão
        </DialogTitle>
        <DialogContent>
          <Typography>Tem certeza que deseja excluir esta multa?</Typography>
          {dialogExclusao.multa && (
            <Box sx={{ mt: 1.5, p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary">Auto de Infração</Typography>
              <Typography fontWeight="bold">{dialogExclusao.multa.auto_infracao || '—'}</Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>Valor</Typography>
              <Typography color="error.main" fontWeight="bold">{fmtValor(dialogExclusao.multa.valor)}</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button onClick={() => setDialogExclusao({ aberto: false, multa: null })}>Cancelar</Button>
          <Button variant="contained" color="error" startIcon={<DeleteIcon />} onClick={excluirMulta}>Excluir</Button>
        </DialogActions>
      </Dialog>

      {/* ── Snackbar ── */}
      <Snackbar open={snackbar.open} autoHideDuration={3500} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
        <Alert onClose={() => setSnackbar(s => ({ ...s, open: false }))} severity={snackbar.tipo}>
          {snackbar.mensagem}
        </Alert>
      </Snackbar>
    </Container>
  );
}
